export interface FolderNode<T = unknown> {
  name: string
  path: string
  children: Map<string, FolderNode<T>>
  shares: T[]
}

/**
 * Build a folder tree from a flat list of shares and preserved folder paths
 */
export function buildFolderTree<T extends { folderPath: string }>(
  shares: T[],
  preservedFolders: ReadonlySet<string>,
): FolderNode<T> {
  // Group shares by folder path
  const folderMap = new Map<string, T[]>()
  for (const share of shares) {
    const path = share.folderPath || '/'
    const existing = folderMap.get(path)
    if (existing) {
      existing.push(share)
    } else {
      folderMap.set(path, [share])
    }
  }

  // Get all unique folder paths (including preserved empty folders) and sort
  const allPaths = Array.from(
    new Set([...folderMap.keys(), ...preservedFolders]),
  ).sort()

  // Build tree
  const root: FolderNode<T> = {
    name: '/',
    path: '/',
    children: new Map(),
    shares: folderMap.get('/') || [],
  }

  for (const path of allPaths) {
    if (path === '/') continue
    const parts = path.split('/').filter(Boolean)
    let current = root
    let currentPath = ''
    for (const part of parts) {
      currentPath += '/' + part
      let child = current.children.get(part)
      if (!child) {
        child = {
          name: part,
          path: currentPath,
          children: new Map(),
          shares: [],
        }
        current.children.set(part, child)
      }
      current = child
    }
    current.shares = folderMap.get(path) || []
  }

  return root
}

/**
 * Count total shares in a folder node (direct children only, not recursive)
 * Matches the original behavior: node.shares.length + direct child shares
 */
export function countFolderShares(node: FolderNode): number {
  return (
    node.shares.length +
    Array.from(node.children.values()).reduce(
      (sum, child) => sum + child.shares.length,
      0,
    )
  )
}

/**
 * Check if a folder is empty and only exists because it was preserved
 */
export function isEmptyPreservedFolder(
  node: FolderNode,
  totalShares: number,
  preservedFolders: ReadonlySet<string>,
): boolean {
  return totalShares === 0 && node.children.size === 0 && preservedFolders.has(node.path)
}
